import { MoreHorizontal, Pause, Play, Trash2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { api } from '../../api';
import { t } from '../../i18n';
import type { FeedItem, PrayerTime } from '../../types';
import { Button } from '../shared/Button';
import { Modal } from '../shared/Modal';
import { useToast } from '../shared/Toast';

const PRAYER_NAME_KEY: Record<PrayerTime, string> = {
  bomdod: 'prayer.bomdod',
  peshin: 'prayer.peshin',
  asr: 'prayer.asr',
  shom: 'prayer.shom',
  xufton: 'prayer.xufton',
};

export type FeedPlayer = {
  activeKey: string | null;
  speed: number;
  autoAdvance: boolean;
  setSpeed: (s: number) => void;
  setAutoAdvance: (v: boolean) => void;
  toggle: (key: string) => void;
  isPlaying: (key: string) => boolean;
  audioRef: React.RefObject<HTMLAudioElement | null>;
};

export function useFeedPlayer(
  items: FeedItem[],
  setItems: React.Dispatch<React.SetStateAction<FeedItem[]>>,
): FeedPlayer {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(1);
  const [autoAdvance, setAutoAdvance] = useState(true);
  const heardRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!audioRef.current) {
      const audio = document.createElement('audio');
      audio.preload = 'metadata';
      audio.style.display = 'none';
      document.body.appendChild(audio);
      audioRef.current = audio;
    }
    audioRef.current.playbackRate = speed;
  }, [speed]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const onTime = () => {
      const key = activeKey;
      if (key == null) return;
      if (audio.duration > 0 && audio.currentTime / audio.duration >= 0.8) {
        if (!heardRef.current.has(key)) {
          heardRef.current.add(key);
          const item = items.find((i) => i.key === key);
          if (item) {
            api.markHeard(item.module, item.id, true).catch(() => {});
            setItems((prev) => prev.map((it) => (it.key === key ? { ...it, parent_heard: true } : it)));
          }
        }
      }
    };

    const onEnd = () => {
      const key = activeKey;
      setPlaying(false);
      if (!autoAdvance || key == null) return;
      const idx = items.findIndex((i) => i.key === key);
      const next = items[idx + 1];
      if (next) {
        setActiveKey(next.key);
        audio.src = next.audio_url;
        audio.playbackRate = speed;
        void audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
      }
    };

    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('ended', onEnd);
    audio.addEventListener('play', () => setPlaying(true));
    audio.addEventListener('pause', () => setPlaying(false));
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('ended', onEnd);
    };
  }, [activeKey, items, autoAdvance, speed, setItems]);

  const toggle = (key: string) => {
    const audio = audioRef.current;
    if (!audio) return;
    if (activeKey === key) {
      if (audio.paused) void audio.play();
      else audio.pause();
      return;
    }
    const item = items.find((i) => i.key === key);
    if (!item) return;
    setActiveKey(key);
    audio.src = item.audio_url;
    audio.playbackRate = speed;
    void audio.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  };

  const isPlaying = (key: string) => activeKey === key && playing;

  return { activeKey, speed, autoAdvance, setSpeed, setAutoAdvance, toggle, isPlaying, audioRef };
}

function fmtDuration(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('uz-UZ', {
    timeZone: 'Asia/Tashkent',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function FeedRow({
  item,
  player,
  onDeleted,
}: {
  item: FeedItem;
  player: FeedPlayer;
  onDeleted?: (key: string) => void;
}) {
  const playing = player.isPlaying(item.key);
  const toast = useToast();
  const [menuOpen, setMenuOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!menuOpen) return;
    const close = () => setMenuOpen(false);
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [menuOpen]);

  const doDelete = async () => {
    setDeleting(true);
    try {
      await api.deleteRecording(item.module, item.id);
      onDeleted?.(item.key);
      toast.show(t('common.deleted'), 'success');
    } catch {
      toast.show(t('common.error.network'), 'danger');
    } finally {
      setDeleting(false);
      setConfirmOpen(false);
    }
  };

  const isMem = item.module === 'memorization';

  return (
    <li
      className={[
        `ramp-${item.child.color_ramp}`,
        'flex items-center gap-3 rounded-md border-hairline border-border bg-surface px-3 py-2.5',
      ].join(' ')}
    >
      <button
        onClick={() => player.toggle(item.key)}
        aria-label={playing ? 'pause' : 'play'}
        className="flex h-9 w-9 items-center justify-center rounded-full bg-child-fill text-child-accent active:scale-[0.97]"
      >
        {playing ? <Pause size={16} /> : <Play size={16} className="ml-0.5" />}
      </button>
      <div className="flex flex-1 flex-col">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[14px]">
          <span className="font-medium">{item.child.name}</span>
          {isMem ? (
            <>
              <span className="rounded-full bg-child-fill px-1.5 py-0.5 text-[10px] font-medium text-child-accent">
                {t('feed.mem.tag')}
              </span>
              <span className="text-ink-muted">
                {item.surah.number}. {item.surah.name_uz} · {item.ayah_from}–{item.ayah_to}-oyat
              </span>
            </>
          ) : (
            <>
              <span className="text-ink-muted">·</span>
              <span>{item.prayer_time ? t(PRAYER_NAME_KEY[item.prayer_time]) : ''}</span>
              <span className="text-ink-muted">·</span>
              <span className="text-ink-muted">
                {item.surah.number}. {item.surah.name_uz}
              </span>
            </>
          )}
        </div>
        <div className="flex items-center gap-2 text-[12px] text-ink-muted tabular-nums">
          <span>{fmtTime(item.recorded_at)}</span>
          <span>·</span>
          <span>{fmtDuration(item.duration_seconds)}</span>
          {isMem && item.rep_index ? (
            <>
              <span>·</span>
              <span>{t('feed.rep', { n: item.rep_index })}</span>
            </>
          ) : null}
        </div>
      </div>
      {!item.parent_heard ? (
        <span className="inline-block h-2 w-2 rounded-full bg-info" aria-label="unheard" />
      ) : null}
      {onDeleted ? (
        <div className="relative">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setMenuOpen((v) => !v);
            }}
            aria-label={t('common.more')}
            className="flex h-8 w-8 items-center justify-center rounded text-ink-muted active:scale-[0.97]"
          >
            <MoreHorizontal size={18} />
          </button>
          {menuOpen ? (
            <div
              onClick={(e) => e.stopPropagation()}
              className="absolute right-0 top-9 z-10 w-40 overflow-hidden rounded-md border-hairline border-border bg-surface shadow"
            >
              <button
                onClick={() => {
                  setMenuOpen(false);
                  setConfirmOpen(true);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-danger active:bg-surface-muted"
              >
                <Trash2 size={14} />
                {t('common.delete')}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      <Modal open={confirmOpen} onClose={() => setConfirmOpen(false)} title={t('parent.delete.confirm.title')}>
        <div className="flex flex-col gap-4">
          <p className="text-[13px] text-ink-muted">{t('parent.delete.confirm.body')}</p>
          <div className="flex flex-col gap-2">
            <Button onClick={doDelete} disabled={deleting} fullWidth>
              {deleting ? t('common.loading') : t('common.delete')}
            </Button>
            <Button variant="ghost" onClick={() => setConfirmOpen(false)} fullWidth>
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      </Modal>
    </li>
  );
}
